import { parse as parseOpenType } from "opentype.js";
import {
  FONT_KIND,
  faceDisplayName,
  normalizeFontName,
  normalizeFontWeight,
  toFaceKey,
  toFontKey,
} from "./fonts";
import {
  getItalicSkipPersist,
  resolveFontFamilyIdentity,
} from "./resolveFontFamilyIdentity";

const englishName = (font, key) => {
  if (typeof font.getEnglishName === "function") {
    const value = font.getEnglishName(key);
    if (value) return String(value);
  }
  const record = font.names?.[key];
  if (!record) return "";
  if (typeof record === "string") return record;
  return (
    record.en ||
    record["en-US"] ||
    Object.values(record).find(Boolean) ||
    ""
  );
};

const instanceName = (instance, index) => {
  const raw = instance?.name;
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  if (raw && typeof raw === "object") {
    return (
      raw.en ||
      raw["en-US"] ||
      Object.values(raw).find((value) => typeof value === "string") ||
      ""
    );
  }
  return `Instance ${index + 1}`;
};

const filterUprightInstances = (namedInstances = {}) =>
  Object.fromEntries(
    Object.entries(namedInstances).filter(([, instance]) => !instance?.italic)
  );

export const parseFontFile = async ({ file } = {}) => {
  if (!file) return { ok: false };
  try {
    const buffer = await file.arrayBuffer();
    const font = parseOpenType(buffer);
    const preferredFamily = normalizeFontName({
      name: englishName(font, "preferredFamily"),
    });
    const fontFamily = normalizeFontName({
      name: englishName(font, "fontFamily"),
    });
    const preferredSubfamily = normalizeFontName({
      name: englishName(font, "preferredSubfamily"),
    });
    const fontSubfamily = normalizeFontName({
      name: englishName(font, "fontSubfamily"),
    });
    const os2 = font.tables?.os2 || {};
    const head = font.tables?.head || {};
    const os2Weight = normalizeFontWeight(os2.usWeightClass);
    const tableItalic =
      Boolean((os2.fsSelection || 0) & 1) ||
      Boolean((head.macStyle || 0) & 2);
    const identity = resolveFontFamilyIdentity({
      preferredFamily,
      fontFamily,
      preferredSubfamily,
      fontSubfamily,
      os2Weight,
      italic: tableItalic,
    });
    const familyName = identity.familyName;
    const subfamily = identity.faceName;
    const weight = identity.weight;
    const italic = identity.italic;
    const fvar = font.tables?.fvar;
    const axesList = fvar?.axes || [];
    if (axesList.length) {
      const axes = {};
      axesList.forEach((axis) => {
        if (!axis?.tag) return;
        axes[axis.tag] = {
          min: axis.minValue,
          max: axis.maxValue,
          default: axis.defaultValue,
        };
      });
      const namedInstances = {};
      (fvar.instances || []).forEach((instance, index) => {
        const name = instanceName(instance, index);
        const coords = instance.coordinates || {};
        const instanceWeight = normalizeFontWeight(
          coords.wght ?? axes.wght?.default ?? weight
        );
        const instanceItalic =
          Number(coords.ital || 0) > 0 || Number(coords.slnt || 0) < 0;
        const key =
          toFontKey({ name }) ||
          toFaceKey({ weight: instanceWeight, italic: instanceItalic });
        namedInstances[key] = {
          key,
          name,
          weight: instanceWeight,
          italic: instanceItalic,
        };
      });
      const uprightInstances = filterUprightInstances(namedInstances);
      const skipPersist = getItalicSkipPersist({
        kind: FONT_KIND.VARIABLE,
        namedInstances: uprightInstances,
      });
      const firstUpright = Object.values(uprightInstances)[0];
      return {
        ok: true,
        familyName,
        subfamily,
        kind: FONT_KIND.VARIABLE,
        weight,
        italic,
        axes,
        namedInstances: uprightInstances,
        faceKey:
          firstUpright?.key ||
          Object.keys(namedInstances)[0] ||
          toFaceKey({ weight, italic }),
        faceName:
          firstUpright?.name ||
          subfamily ||
          faceDisplayName({ weight, italic }),
        ...(skipPersist ? { skipPersist } : {}),
      };
    }
    const skipPersist = getItalicSkipPersist({
      kind: FONT_KIND.STATIC,
      italic,
    });
    return {
      ok: true,
      familyName,
      subfamily,
      kind: FONT_KIND.STATIC,
      weight,
      italic,
      faceKey: toFaceKey({ weight, italic }),
      faceName: subfamily || faceDisplayName({ weight, italic }),
      ...(skipPersist ? { skipPersist } : {}),
    };
  } catch (_error) {
    return { ok: false };
  }
};
